import React, { useState } from 'react';
import { Typography, Box, Avatar, Chip } from '@mui/material';
import { Circle as CircleIcon, Person as PersonIcon } from '@mui/icons-material';

import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useTheme } from '@mui/material/styles';

const initialsFromName = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
};

const PRIORITY_COLOR = { high: 'error', medium: 'warning', low: 'default' };

export default function TodoAssignedNotificationCard({ notification, onMarkRead }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const payload = notification.payload || {};
  const [imgBroken, setImgBroken] = useState(false);

  // The assigner may be unknown (automatic assignment); the card must still render,
  // or the badge counts a notification the list never shows.
  if (!payload.proposal_id) return null;
  const assignerAvatarSrc = payload.assigner_avatar || payload.assigner_avatar_url || null;
  const showInitials = !assignerAvatarSrc || imgBroken;

  const handleClick = () => {
    if (!notification.is_read && onMarkRead) onMarkRead();
    if (payload.proposal_id) navigate(`/proposal/${payload.proposal_id}`);
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        py: 1, px: 2,
        borderBottom: '1px solid', borderColor: 'divider',
        backgroundColor: notification.is_read ? 'transparent' : 'action.hover',
        cursor: 'pointer',
        '&:hover': { backgroundColor: 'action.selected' },
      }}
    >
      <Avatar
        src={showInitials ? undefined : assignerAvatarSrc}
        onError={() => setImgBroken(true)}
        sx={{ width: 32, height: 32, bgcolor: theme.palette.success.main, color: 'white', fontSize: 13 }}
      >
        {payload.assigner_name ? initialsFromName(payload.assigner_name) : <PersonIcon sx={{ fontSize: 18 }} />}
      </Avatar>

      <Typography variant="body2" sx={{ flex: 1, flexWrap: 'wrap' }}>
        <Box component="span" fontWeight={600}>To-do assigned to you</Box>
        {payload.todo_title && (
          <Box component="span" color="text.secondary"> — {payload.todo_title}</Box>
        )}
        {payload.proposal_acronym && (
          <Box component="span" color="text.secondary"> in {payload.proposal_acronym}</Box>
        )}
        {payload.assigner_name && (
          <>
            <Box component="span" color="text.secondary"> by </Box>
            <Box component="span" fontWeight={500} color="text.primary">{payload.assigner_name}</Box>
          </>
        )}
        {payload.priority && (
          <Chip
            label={payload.priority}
            size="small"
            color={PRIORITY_COLOR[payload.priority] || 'default'}
            sx={{ ml: 0.75, height: 16, fontSize: '0.65rem' }}
          />
        )}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {formatDistanceToNow(new Date(notification.created), { addSuffix: true })}
        </Typography>
        {!notification.is_read && <CircleIcon sx={{ fontSize: 8, color: 'primary.main' }} />}
      </Box>
    </Box>
  );
}
